import { useCreateDialogLookModal } from 'components/create-dialog/CreateDialogModalFrame';
import { Button } from 'components/ui/button';
import { ReactNode, useCallback } from 'react';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoiceRowForm, InvoiceRowTotals } from 'types/invoices';
import { getClassifierText } from 'utils/getTranslations';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { AdminFeeEditModal } from './AdminFeeEditModal';
import { totalsButtonStyle } from '../TotalsTableCard';

const FORM_OVERFLOW_CUSTOM_STYLE = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    width: '700px',
};

interface Props {
    children: ReactNode;
    matterId: number;
    invoice: InvoiceData;
    handleSubmit: (data: InvoiceRowForm) => void;
    handleDelete: (invoiceRowId: number) => void;
    invoiceRowTotals: InvoiceRowTotals;
    baseAmount: number;
    classifiers: Record<string, ClassifierData>;
}

const useAdminFeeEditModal = (
    invoice: InvoiceData,
    matterId: number,
    handleSubmit: (data: InvoiceRowForm) => void,
    handleDelete: (invoiceRowId: number) => void,
    invoiceRowTotals: InvoiceRowTotals,
    baseAmount: number,
    classifiers: Record<string, ClassifierData>
) => {
    const { openModal, closeModal } = useCreateDialogLookModal();
    const { isPaymentCurrencyMode } = usePaymentCurrencyMode(invoice);

    const invoiceLang = invoice.invoiceTemplate?.primaryLanguageTypeCode ?? 'eng';
    const adminFeeLabel = getClassifierText(classifiers['INVOICE_ROW_TYPE'], 'ADMIN_FEE', undefined, invoiceLang);

    const openEditModal = useCallback(() => {
        const itemTotal = isPaymentCurrencyMode
            ? (invoiceRowTotals.itemTotalInPaymentCurrency ?? 0)
            : (invoiceRowTotals.itemTotal ?? 0);
        openModal(
            <AdminFeeEditModal
                onClose={closeModal}
                action={'update'}
                invoice={invoice}
                invoiceRowTotals={invoiceRowTotals}
                baseAmount={baseAmount}
                initialData={{
                    invoiceRowId: invoiceRowTotals.invoiceRowId ?? 0,
                    matterId: matterId ?? 0,
                    description: invoiceRowTotals.description ?? '',
                    itemTotal,
                    discountRate: 0,
                    discountAmount: '0',
                    amountBeforeDiscount: baseAmount,
                    adminFeeRate: invoiceRowTotals.adminFeeRate ?? 0,
                }}
                onSubmit={handleSubmit}
                onDelete={handleDelete}
                autoSyncDescription
                adminFeeLabel={adminFeeLabel}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    }, [
        closeModal,
        handleDelete,
        handleSubmit,
        invoiceRowTotals,
        invoice,
        matterId,
        openModal,
        baseAmount,
        isPaymentCurrencyMode,
        adminFeeLabel,
    ]);

    return { openEditModal };
};

export const AdminFeeEditButton = (props: Props) => {
    const { handleSubmit, handleDelete, invoice, matterId, invoiceRowTotals, baseAmount, classifiers, children } =
        props;

    const { openEditModal } = useAdminFeeEditModal(
        invoice,
        matterId,
        handleSubmit,
        handleDelete,
        invoiceRowTotals,
        baseAmount,
        classifiers
    );

    return (
        <Button {...totalsButtonStyle('w-[45px]')} onClick={openEditModal}>
            {children}
        </Button>
    );
};
